import React, { useRef, useEffect, useMemo } from 'react';
import {
  Dialog,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  IconButton,
  Chip,
} from '@mui/material';
import {
  Compare as CompareIcon,
  Close as CloseIcon,
} from '@mui/icons-material';
import { DiffEditor } from '@monaco-editor/react';
import type * as Monaco from 'monaco-editor';

interface ExecutionAction {
  action: string;
  action_type?: string;
  action_id: string;
  element?: string;
  element_id: string | null;
  element_name?: string | null;
  element_type: string;
  target_file?: string;
  file?: string | null;
  files_affected?: string[];
  description?: string;
  status: 'completed' | 'failed';
  error: string | null;
  duration_seconds: number;
  xml_before: string;
  xml_after: string;
}

interface XMLDiffDialogProps {
  open: boolean;
  action: ExecutionAction | null;
  onClose: () => void;
}

/**
 * Pretty-print XML with consistent indentation.
 * Handles self-closing tags, opening/closing tags, and preserves text content.
 */
function formatXml(xml: string | null | undefined): string {
  if (!xml) return '';

  // Remove existing whitespace between tags
  let formatted = xml.replace(/>\s*</g, '><').trim();

  let result = '';
  let indent = 0;
  const pad = '  '; // 2-space indent

  // Split into tags and text
  const tokens = formatted.match(/(<[^>]+>)|([^<]+)/g) || [];

  for (const token of tokens) {
    if (!token.trim()) continue;

    if (token.startsWith('</')) {
      // Closing tag: decrease indent first
      indent = Math.max(0, indent - 1);
      result += pad.repeat(indent) + token + '\n';
    } else if (token.startsWith('<') && token.endsWith('/>')) {
      // Self-closing tag: same indent
      result += pad.repeat(indent) + token + '\n';
    } else if (token.startsWith('<?')) {
      // XML declaration
      result += token + '\n';
    } else if (token.startsWith('<!--')) {
      // Comment
      result += pad.repeat(indent) + token + '\n';
    } else if (token.startsWith('<') && !token.startsWith('</')) {
      // Opening tag: print then increase indent
      result += pad.repeat(indent) + token + '\n';
      indent++;
    } else {
      // Text content: trim and indent
      const text = token.trim();
      if (text) {
        result += pad.repeat(indent) + text + '\n';
      }
    }
  }

  return result.trimEnd();
}

/**
 * Get the action type label from the action data
 */
function getActionTypeLabel(action: ExecutionAction): string {
  const actionType = action.action_type || action.action || '';
  if (actionType === 'download_gl') return 'DOWNLOAD GL';
  if (actionType === 'create_custom_form') return 'CREATE FORM';
  if (actionType.startsWith('add_')) return 'ADD';
  if (actionType.startsWith('modify_')) return 'MODIFY';
  if (actionType.startsWith('remove_')) return 'REMOVE';
  return actionType.toUpperCase().replace(/_/g, ' ') || '';
}

/**
 * Resolve the display name for the element
 */
function getElementFullDisplay(action: ExecutionAction): string {
  const name = action.element_name || action.element || '';
  const id = action.element_id || '';
  if (name && id) return `${name} (${id})`;
  return name || id || 'Unknown';
}

/**
 * Resolve the file name from the action data
 */
function getResolvedFile(action: ExecutionAction): string {
  return (
    action.file ||
    action.target_file ||
    (action.files_affected && action.files_affected.length > 0
      ? action.files_affected[0]
      : '') ||
    ''
  );
}

export const XMLDiffDialog: React.FC<XMLDiffDialogProps> = ({
  open,
  action,
  onClose,
}) => {
  const editorRef = useRef<Monaco.editor.IStandaloneDiffEditor | null>(null);

  // Cleanup editor on unmount or when dialog closes
  useEffect(() => {
    return () => {
      if (editorRef.current) {
        editorRef.current.dispose();
        editorRef.current = null;
      }
    };
  }, []);

  // Reset editor when dialog closes
  useEffect(() => {
    if (!open && editorRef.current) {
      editorRef.current.dispose();
      editorRef.current = null;
    }
  }, [open]);

  // Format XML with proper indentation
  const formattedBefore = useMemo(
    () => (action ? formatXml(action.xml_before) : ''),
    [action]
  );
  const formattedAfter = useMemo(
    () => (action ? formatXml(action.xml_after) : ''),
    [action]
  );

  const handleEditorDidMount = (editor: Monaco.editor.IStandaloneDiffEditor) => {
    editorRef.current = editor;
  };

  if (!action) {
    return null;
  }

  const actionLabel = getActionTypeLabel(action);
  const elementDisplay = getElementFullDisplay(action);
  const fileDisplay = getResolvedFile(action);

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="lg"
      fullWidth
      PaperProps={{
        sx: { height: '85vh', borderRadius: 2, overflow: 'hidden' },
      }}
    >
      {/* Header (matching Flutter: icon + title + subtitle + close button) */}
      <Box
        sx={{
          px: 2.5,
          py: 2,
          display: 'flex',
          alignItems: 'center',
          gap: 1.5,
          bgcolor: 'action.hover',
          borderBottom: 1,
          borderColor: 'divider',
        }}
      >
        <CompareIcon sx={{ color: 'primary.main', fontSize: 24 }} />
        <Box sx={{ flex: 1 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
            <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
              Code Comparison: {elementDisplay}
            </Typography>
            {action.element_type && (
              <Chip
                label={action.element_type}
                size="small"
                color="primary"
                sx={{ fontSize: '0.7rem', height: 22, fontWeight: 600 }}
              />
            )}
          </Box>
          <Typography variant="caption" color="text.secondary">
            {actionLabel}{fileDisplay ? ` • ${fileDisplay}` : ''}
          </Typography>
        </Box>
        <IconButton onClick={onClose} size="small" sx={{ color: 'text.secondary' }}>
          <CloseIcon />
        </IconButton>
      </Box>

      {/* BEFORE / AFTER labels */}
      <Box sx={{ display: 'flex', borderBottom: 1, borderColor: 'divider' }}>
        <Box
          sx={{
            flex: 1,
            py: 0.75,
            textAlign: 'center',
            bgcolor: (theme) =>
              theme.palette.mode === 'dark'
                ? 'rgba(211,47,47,0.08)'
                : 'rgba(211,47,47,0.06)',
          }}
        >
          <Typography
            variant="caption"
            sx={{ fontWeight: 700, color: 'error.main', letterSpacing: 1 }}
          >
            BEFORE
          </Typography>
        </Box>
        <Box
          sx={{
            flex: 1,
            py: 0.75,
            textAlign: 'center',
            bgcolor: (theme) =>
              theme.palette.mode === 'dark'
                ? 'rgba(46,125,50,0.08)'
                : 'rgba(46,125,50,0.06)',
          }}
        >
          <Typography
            variant="caption"
            sx={{ fontWeight: 700, color: 'success.main', letterSpacing: 1 }}
          >
            AFTER
          </Typography>
        </Box>
      </Box>

      {/* Diff editor */}
      <DialogContent sx={{ p: 0, overflow: 'hidden' }}>
        <Box sx={{ height: '100%' }}>
          <DiffEditor
            original={formattedBefore}
            modified={formattedAfter}
            language="xml"
            theme="vs-dark"
            onMount={handleEditorDidMount}
            options={{
              readOnly: true,
              renderSideBySide: true,
              minimap: { enabled: false },
              scrollBeyondLastLine: false,
              wordWrap: 'on',
              automaticLayout: true,
              fontSize: 13,
              lineHeight: 20,
              padding: { top: 12, bottom: 12 },
              renderIndicators: true,
              renderOverviewRuler: false,
              scrollbar: {
                verticalScrollbarSize: 8,
                horizontalScrollbarSize: 8,
              },
            }}
          />
        </Box>
      </DialogContent>

      {/* Footer */}
      <DialogActions
        sx={{
          px: 2.5,
          py: 1.5,
          borderTop: 1,
          borderColor: 'divider',
        }}
      >
        <Button
          onClick={onClose}
          variant="contained"
          size="small"
          sx={{ textTransform: 'none', minWidth: 80 }}
        >
          Close
        </Button>
      </DialogActions>
    </Dialog>
  );
};
